import React from 'react';
import { Box } from 'edenred-ui';
import { useTranslation, Trans } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useTheme } from 'styled-components';

import { FormFields } from '@epi-forms/helpers';
import {
  maxLength,
  socialSecurityNumber,
  required as requiredValidation
} from '@epi-helpers/formHelpers/validators';
import { TerminationDate } from '@epi-pages/Beneficiaries/components/TerminationDate';
import {
  FirstNameField,
  LastNameField,
  EmailAddressField
} from '@epi-forms/controls';
import { EmployeeTypeModes } from '@epi-constants/beneficiary';
import { useManageBenefits } from '@epi-containers/ManageBenefits/hooks';
import { hasDatePassed, parseStringToDate } from '@epi-helpers/dateHelper';

import { PersonalDataField } from '../PersonalDataField';
import { EntitledDataField } from '../EntitledDataField';
import { EmployeeTypeDataField } from '../EmployeeTypeDataField';
import { ContractPercentageField } from '../ContractPercentageField';
import { DaysForLunariField } from '../DaysForLunariField';

type OwnProps = {
  isAdd: boolean;
};
export const PersonalData = ({ isAdd }: OwnProps) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const { isLunchBenefitActive } = useManageBenefits();
  const EoF = watch(FormFields.EndOfFunds);
  const employeeType = watch(FormFields.EmployeeType);
  const isPartTimeOrTemporaryType = [
    EmployeeTypeModes.PartTime,
    EmployeeTypeModes.TemporaryEmployed
  ].includes(employeeType);
  const isOnHold = employeeType === EmployeeTypeModes.Onhold;
  const isDropped = employeeType === EmployeeTypeModes.Dropped;
  const isPartTime = employeeType === EmployeeTypeModes.PartTime;
  const { sizeExtraSmall } = useTheme();
  const endOfFundsDatePassed = !!EoF && !!hasDatePassed(parseStringToDate(EoF));

  return (
    <Box
      p={0}
      pt={4}
      display="flex"
      flexDirection="column"
      gap={3}
      width="100%"
      id="employee-benefit-personal-data-tab"
    >
      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        columnGap={6}
        rowGap={4}
      >
        <FirstNameField readOnly={endOfFundsDatePassed} />
        <LastNameField readOnly={endOfFundsDatePassed} />

        <PersonalDataField
          label={t('containers.beneficiaries.detail.personal_id')}
          name={FormFields.PersonalID}
          required={isAdd}
          validate={
            isAdd
              ? [requiredValidation, maxLength(50), socialSecurityNumber]
              : undefined
          }
          disabled={!isAdd || endOfFundsDatePassed}
        />
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        columnGap={6}
        rowGap={4}
      >
        <EmailAddressField
          label={t('containers.beneficiaries.table.email')}
          readOnly={endOfFundsDatePassed}
        />

        <EntitledDataField isRemoved={EoF && !isAdd} isAddMode={isAdd} />
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        columnGap={6}
        rowGap={4}
      >
        <Box>
          <EmployeeTypeDataField isAddMode={isAdd} />
          {isOnHold && isAdd && (
            <Box
              mt={1}
              fontSize={sizeExtraSmall}
              id="add_beneficiary_employee_type_subtext"
            >
              {t('containers.beneficiaries.detail.employee_type_subtext')}
            </Box>
          )}
        </Box>

        <Box>
          <ContractPercentageField
            disabled={!isPartTimeOrTemporaryType || endOfFundsDatePassed}
            isAddMode={isAdd}
            isPartTime={isPartTime}
          />
          <Box mt={1} fontSize={12} id="add_beneficiary_working_hours_subtext">
            {t('containers.beneficiaries.detail.working_hours_subtext')}
          </Box>
        </Box>

        {isLunchBenefitActive ? (
          <Box>
            <DaysForLunariField
              disabled={(isOnHold || isDropped) && !isAdd}
              isAddMode={isAdd}
            />
            <Box
              mt={1}
              fontSize={sizeExtraSmall}
              id="add_beneficiary_lunch_benefit_per_month_subtext"
            >
              <Trans
                defaults={t(
                  'containers.beneficiaries.detail.lunch_benefit_per_month_subtext'
                )}
                components={{ b: <b /> }}
              />
            </Box>
          </Box>
        ) : (
          <Box />
        )}
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        columnGap={6}
        rowGap={4}
      >
        <PersonalDataField
          label={t('containers.beneficiaries.detail.employee_number')}
          name={FormFields.EmployeeNumber}
          validate={[maxLength(20)]}
          disabled={endOfFundsDatePassed}
        />

        <PersonalDataField
          label={t('containers.beneficiaries.detail.cost_center_number')}
          name={FormFields.CostCenterNumber}
          validate={[maxLength(50)]}
          disabled={endOfFundsDatePassed}
        />
      </Box>

      {!isAdd && (
        <PersonalDataField
          label={t('containers.beneficiaries.detail.create_data')}
          name={FormFields.CreatedAt}
          disabled={!isAdd || endOfFundsDatePassed}
        />
      )}

      <TerminationDate
        showTerminationButton={!endOfFundsDatePassed}
        isAddMode={isAdd}
      />
    </Box>
  );
};
